function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Loader from '../Loader';
import { useAuthAPI } from '../../common/hooks/useAuthAPI';
import FilePreview, { imagesExt } from '../FilePreview';
import facade from '../../common/utils/facade';
import Icon from '../Icon';
import MultiFetch, { useFileKeyLoaderMultiFetch } from './MultiFetch';
import './index.scss';
export const CACHEDFILEKEYS = {};
export const useFilekeyLoader = ({
  endpoint,
  fileKey = "",
  cache,
  cacheUniqueString,
  cacheExpiration,
  isSureImage,
  payload
}) => {
  const multipleFetch = useFileKeyLoaderMultiFetch();
  const [{
    loaded,
    loaderror
  }, setStates] = useState({
    loaded: false,
    loaderror: false
  });
  const requestFileURL = useAuthAPI(endpoint);
  const isImage = isSureImage || imagesExt.includes(fileKey.split('.').pop() || "not-image");
  const cacheKey = fileKey + cacheUniqueString;
  useEffect(() => {
    if (multipleFetch.loading === true) return;

    if (cacheKey in CACHEDFILEKEYS && !!cacheExpiration) {
      const currentInterval = Date.now() - CACHEDFILEKEYS[cacheKey].timeStamp;

      if (currentInterval > cacheExpiration) {
        delete CACHEDFILEKEYS[cacheKey];
      }
    }

    if (!(cacheKey in CACHEDFILEKEYS) && fileKey && isImage) {
      requestFileURL.call({
        data: {
          fileKey,
          thumbnail: fileKey.includes('upload'),
          ...payload
        }
      }).then(response => {
        if (response.data.url && cache) {
          CACHEDFILEKEYS[cacheKey] = {
            url: response.data.url,
            timeStamp: Date.now()
          };
        }
      }).catch(() => {
        setStates(prev => ({ ...prev,
          loaded: true,
          loaderror: true
        }));
      });
    }
  }, [cacheKey, multipleFetch.loading]);

  const handleLoad = () => {
    setStates(prev => ({ ...prev,
      loaded: true
    }));
  };

  const handleLoadError = () => {
    setStates(prev => ({ ...prev,
      loaded: true,
      loaderror: true
    }));
  };

  const url = CACHEDFILEKEYS[cacheKey]?.url || requestFileURL.response?.url;
  return {
    handleLoad,
    handleLoadError,
    isImage,
    loaderror,
    loaded,
    url
  };
};
// @ts-ignore
const FileKeyLoader = /*#__PURE__*/memo(({
  fileKey = "",
  types,
  transparent,
  endpoint,
  cache,
  cacheExpiration,
  cacheUniqueString,
  payload,
  isSureImage,
  facadeStyle,
  fallbackSrc,
  fallbackNode,
  loaderProps,
  onLoaded,
  ...props
}) => {
  const ref = useRef(null);
  const {
    loaded,
    loaderror,
    isImage,
    url,
    handleLoad,
    handleLoadError
  } = useFilekeyLoader({
    fileKey,
    cache,
    cacheExpiration,
    cacheUniqueString,
    endpoint,
    payload,
    isSureImage
  });
  useLayoutEffect(() => {
    if (isImage) {
      if (loaded) {
        if (onLoaded) {
          onLoaded();
        }

        const clean = setTimeout(() => ref.current?.parentElement?.classList.remove('file-parent'), 150);
        return () => clearTimeout(clean);
      } else {
        ref.current?.parentElement?.classList.add('file-parent');
      }
    }
  }, [loaded]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, !fileKey ? null : isImage ? loaderror ? fallbackNode || /*#__PURE__*/React.createElement("img", _extends({}, props, {
    src: fallbackSrc || "error"
  })) : /*#__PURE__*/React.createElement("img", _extends({
    ref: ref
  }, props, {
    src: url === null ? "image" : url,
    onLoad: handleLoad,
    onError: handleLoadError,
    style: loaded ? props.style : {
      'display': 'none'
    }
  })) : /*#__PURE__*/React.createElement("div", props, /*#__PURE__*/React.createElement(FilePreview, {
    types: types,
    filename: fileKey
  })), isImage && !loaded && /*#__PURE__*/React.createElement("img", _extends({}, props, {
    style: { ...props.style,
      ...facadeStyle
    },
    "data-facade": "true",
    src: facade
  })), /*#__PURE__*/React.createElement(Loader, _extends({}, loaderProps, {
    active: isImage ? !loaded : false,
    style: {
      zIndex: 0,
      ...loaderProps?.style
    },
    transparent: transparent
  })));
});
FileKeyLoader.defaultProps = {
  endpoint: '/api/FileURL',
  payload: {
    forceDownload: false
  },
  cache: false,
  cacheUniqueString: '',
  types: {
    video: /*#__PURE__*/React.createElement(Icon, {
      size: 100,
      name: "video"
    }),
    default: /*#__PURE__*/React.createElement(Icon, {
      size: 100,
      name: "file"
    })
  }
};
FileKeyLoader.MultiFetch = MultiFetch;
export default FileKeyLoader;